const TOKEN_BOUNDARY = '(?=$|\\s|[.,;:!?])';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatLocalTime(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function formatLocalDateTime(date: Date): string {
  return `${formatLocalDate(date)} ${formatLocalTime(date)}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function getClientUserLabel(): string {
  if (typeof window === 'undefined') return 'me';
  try {
    const keys = Object.keys(window.localStorage).filter((key) => key.toLowerCase().includes('auth0'));
    for (const key of keys) {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as {
          body?: { decodedToken?: { user?: { name?: string; email?: string } } };
          decodedToken?: { user?: { name?: string; email?: string } };
          user?: { name?: string; email?: string };
          name?: string;
          email?: string;
        };
        const user = parsed?.body?.decodedToken?.user ?? parsed?.decodedToken?.user ?? parsed?.user ?? parsed;
        const name = user?.name?.trim();
        if (name) return name;
        const email = user?.email?.trim();
        if (email) return email;
      } catch {
        // Ignore malformed auth cache rows.
      }
    }
  } catch {
    // localStorage can be unavailable in strict browser privacy modes.
  }
  return 'me';
}

function replaceToken(value: string, token: string, replacement: string): string {
  const tokenPattern = new RegExp(`(^|\\s)${escapeRegExp(token)}${TOKEN_BOUNDARY}`, 'gi');
  return value.replace(tokenPattern, (_match, leadingWhitespace: string) => `${leadingWhitespace}${replacement}`);
}

/**
 * Expands slash-style text utilities used in plain form inputs.
 *
 * Examples:
 * - /current-date -> 2026-04-06
 * - /today        -> 2026-04-06
 */
export function applyTextUtilities(value: string): string {
  if (!value || !value.includes('/')) return value;
  const now = new Date();
  const today = formatLocalDate(now);
  const me = getClientUserLabel();
  let next = value;
  next = replaceToken(next, '/me', me);
  next = replaceToken(next, '/current-date', today);
  next = replaceToken(next, '/today', today);
  next = replaceToken(next, '/current-time', formatLocalTime(now));
  next = replaceToken(next, '/current-datetime', formatLocalDateTime(now));
  next = replaceToken(next, '/now', formatLocalDateTime(now));
  next = replaceToken(next, '/current-year', String(now.getFullYear()));
  next = replaceToken(next, '/current-month', String(now.getMonth() + 1).padStart(2, '0'));
  next = replaceToken(next, '/start-of-month', formatLocalDate(startOfMonth(now)));
  next = replaceToken(next, '/end-of-month', formatLocalDate(endOfMonth(now)));
  next = replaceToken(next, '/next-week', formatLocalDate(addDays(now, 7)));
  next = next.replace(/(^|\s)\/date([+-]\d{1,3})(?=$|\s|[.,;:!?])/gi, (_m, ws: string, offsetText: string) => {
    const days = Number.parseInt(offsetText, 10);
    if (!Number.isFinite(days)) return _m;
    return `${ws}${formatLocalDate(addDays(now, days))}`;
  });
  return next;
}

export const TEXT_UTILITY_COMMANDS: ReadonlyArray<{ token: string; description: string }> = [
  { token: '/me', description: 'Insert your profile name/email (when available)' },
  { token: '/current-date', description: 'Insert local date (YYYY-MM-DD)' },
  { token: '/today', description: 'Insert local date (YYYY-MM-DD)' },
  { token: '/current-time', description: 'Insert local time (HH:mm)' },
  { token: '/current-datetime', description: 'Insert local date-time' },
  { token: '/now', description: 'Insert local date-time' },
  { token: '/current-year', description: 'Insert current year' },
  { token: '/current-month', description: 'Insert current month (MM)' },
  { token: '/start-of-month', description: 'Insert first day of current month' },
  { token: '/end-of-month', description: 'Insert last day of current month' },
  { token: '/next-week', description: 'Insert date 7 days from now' },
  { token: '/date+7', description: 'Insert date offset by +N days' },
  { token: '/date-30', description: 'Insert date offset by -N days' },
];

export function getTextUtilityCommandNames(): string[] {
  return TEXT_UTILITY_COMMANDS.map((command) => command.token);
}

type ActiveUtilityContext = {
  query: string;
  tokenStart: number;
  tokenEnd: number;
};

function resolveTokenReplacement(token: string, now = new Date()): string | null {
  const normalized = token.toLowerCase();
  if (normalized === '/me') return getClientUserLabel();
  if (normalized === '/current-date' || normalized === '/today') return formatLocalDate(now);
  if (normalized === '/current-time') return formatLocalTime(now);
  if (normalized === '/current-datetime' || normalized === '/now') return formatLocalDateTime(now);
  if (normalized === '/current-year') return String(now.getFullYear());
  if (normalized === '/current-month') return String(now.getMonth() + 1).padStart(2, '0');
  if (normalized === '/start-of-month') return formatLocalDate(startOfMonth(now));
  if (normalized === '/end-of-month') return formatLocalDate(endOfMonth(now));
  if (normalized === '/next-week') return formatLocalDate(addDays(now, 7));

  const dateOffset = normalized.match(/^\/date([+-]\d{1,3})$/);
  if (dateOffset) {
    const days = Number.parseInt(dateOffset[1], 10);
    if (Number.isFinite(days)) return formatLocalDate(addDays(now, days));
  }
  return null;
}

export function findActiveTextUtilityContext(value: string, cursorPosition: number): ActiveUtilityContext | null {
  if (!value || cursorPosition < 0 || cursorPosition > value.length) return null;
  const leftPart = value.slice(0, cursorPosition);
  const match = leftPart.match(/(^|\s)(\/[^\s]*)$/);
  if (!match?.[2]) return null;
  const token = match[2];
  const tokenStart = cursorPosition - token.length;
  return {
    query: token.slice(1).toLowerCase(),
    tokenStart,
    tokenEnd: cursorPosition,
  };
}

export function getFilteredTextUtilityCommands(query: string): ReadonlyArray<{ token: string; description: string }> {
  const needle = query.trim().toLowerCase();
  if (!needle) return TEXT_UTILITY_COMMANDS;
  return TEXT_UTILITY_COMMANDS.filter((command) => {
    const haystack = `${command.token} ${command.description}`.toLowerCase();
    return haystack.includes(needle);
  });
}

export function applySelectedTextUtility(
  value: string,
  context: ActiveUtilityContext,
  token: string,
): { nextValue: string; nextCursor: number } | null {
  const replacement = resolveTokenReplacement(token);
  if (!replacement) return null;
  const nextValue = `${value.slice(0, context.tokenStart)}${replacement}${value.slice(context.tokenEnd)}`;
  const nextCursor = context.tokenStart + replacement.length;
  return { nextValue, nextCursor };
}
